/**
 * Friendly searchable WBS/CBS picker for dense budget grids.
 * Prefer flat searchable Select over cramped TreeSelect in table cells.
 */
import React, { useMemo } from 'react';
import { Select, Typography } from 'antd';
import type { SelectProps } from 'antd';
import type { Est3WbsNode } from '@/utils/est3WbsTree';

const { Text } = Typography;

export type BudgetCodeNode = Est3WbsNode & {
  title?: string;
};

function nodeDepth(
  id: string,
  byId: Map<string, BudgetCodeNode>,
  memo: Map<string, number>
): number {
  if (memo.has(id)) return memo.get(id)!;
  const n = byId.get(id);
  const parentId = n?.parent_id || n?.parentId || null;
  if (!parentId || !byId.has(parentId)) {
    memo.set(id, 0);
    return 0;
  }
  const d = nodeDepth(parentId, byId, memo) + 1;
  memo.set(id, d);
  return d;
}

function displayName(n: BudgetCodeNode): string {
  const name = String(n.name || n.title || '').trim();
  const code = String(n.code || '').trim();
  if (name && name.toLowerCase() !== code.toLowerCase()) return name;
  return name || code || 'Untitled';
}

export interface BudgetCodeSelectProps extends Omit<SelectProps, 'options' | 'children'> {
  nodes: BudgetCodeNode[];
  kind?: 'wbs' | 'cbs';
}

export const BudgetCodeSelect: React.FC<BudgetCodeSelectProps> = ({
  nodes,
  kind = 'wbs',
  placeholder,
  style,
  notFoundContent,
  ...rest
}) => {
  const options = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const depthMemo = new Map<string, number>();
    const sorted = [...nodes].sort((a, b) => {
      const da = nodeDepth(a.id, byId, depthMemo);
      const db = nodeDepth(b.id, byId, depthMemo);
      if (da !== db) return da - db;
      return String(a.code || '').localeCompare(String(b.code || ''));
    });

    return sorted.map((n) => {
      const depth = nodeDepth(n.id, byId, depthMemo);
      const code = String(n.code || '').trim() || '—';
      const name = displayName(n);
      const indent = depth > 0 ? `${'· '.repeat(Math.min(depth, 6))}` : '';
      const labelText = `${indent}${code} — ${name}`;
      return {
        value: n.id,
        label: labelText,
        search: `${code} ${name} ${labelText}`.toLowerCase(),
        code,
        name,
        depth,
      };
    });
  }, [nodes]);

  return (
    <Select
      showSearch
      allowClear
      optionFilterProp="search"
      filterOption={(input, option) => {
        const q = input.trim().toLowerCase();
        if (!q) return true;
        return String((option as any)?.search || (option as any)?.label || '')
          .toLowerCase()
          .includes(q);
      }}
      placeholder={placeholder || (kind === 'cbs' ? 'Search CBS code or name' : 'Search WBS code or name')}
      options={options}
      optionRender={(opt) => {
        const data = opt.data as { code?: string; name?: string; depth?: number };
        const pad = Math.min(Number(data.depth || 0), 6) * 12;
        return (
          <div style={{ paddingLeft: pad, lineHeight: 1.35 }}>
            <Text code style={{ fontSize: 12 }}>
              {data.code}
            </Text>{' '}
            <Text style={{ fontSize: 13 }}>{data.name}</Text>
          </div>
        );
      }}
      getPopupContainer={() => document.body}
      popupMatchSelectWidth={false}
      dropdownStyle={{ minWidth: kind === 'cbs' ? 420 : 480, maxHeight: 360 }}
      listHeight={320}
      style={{ width: '100%', minWidth: 160, ...style }}
      {...rest}
      notFoundContent={
        notFoundContent !== undefined
          ? notFoundContent
          : nodes.length === 0
            ? kind === 'cbs'
              ? 'No CBS codes for this project'
              : 'No WBS nodes — bootstrap or import first'
            : undefined
      }
    />
  );
};

export default BudgetCodeSelect;
